import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem,
  Select, Stack, Tooltip, Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import type { EstadoIntento, IntentoConversion, LineaRegistro, NivelRegistro, OrigenIntento } from '../lib/api.js';
import { formatearDuracion, useEventos } from '../lib/eventos.js';

function describirEstado(e: EstadoIntento): { texto: string; color: 'default' | 'info' | 'success' | 'warning' | 'error'; nota: string } {
  switch (e) {
    case 'en_curso':
      return {
        texto: 'En curso',
        color: 'info',
        nota: 'Se está escribiendo ahora mismo: las líneas van apareciendo solas conforme ffmpeg las escribe.',
      };
    case 'ok':
      return { texto: 'Terminó bien', color: 'success', nota: 'La película quedó esperando en la carpeta de conversiones.' };
    case 'fallo':
      return { texto: 'Falló', color: 'error', nota: 'El original está a salvo: reintentar no vuelve a descargar nada.' };
    case 'cancelado':
      return {
        texto: 'Interrumpido',
        color: 'warning',
        nota: 'No llegó a terminar: la aplicación se detuvo o empezó otro intento encima.',
      };
  }
}

function describirOrigen(o: OrigenIntento): string {
  switch (o) {
    case 'automatico':
      return 'automático al terminar la descarga';
    case 'reintento':
      return 'reintento a mano';
    case 'reconversion':
      return 'reconversión con las pistas editadas';
    case 'altura':
      return 'reconversión automática: pasaba de la altura máxima';
    case 'revision':
      return 'conversión de un fichero de la biblioteca, pedida desde la revisión';
  }
}

/** El color de cada línea. `ffmpeg` va apagado a propósito: son cientos de líneas suyas y
 *  dos nuestras, y las nuestras son las que cuentan qué se decidió. */
function colorDe(nivel: NivelRegistro): string {
  switch (nivel) {
    case 'error':
      return 'error.main';
    case 'aviso':
      return 'warning.main';
    case 'ffmpeg':
      return 'text.secondary';
    case 'info':
      return 'text.primary';
  }
}

function horaDe(ts: string): string {
  return new Date(ts).toLocaleTimeString('es-ES');
}

/** El registro de un intento, con lo ya guardado y lo que va llegando en vivo.
 *
 *  Se mezclan por número de línea: la foto que se pidió por HTTP y las líneas del evento
 *  numeran igual, así que lo que venga en las dos entra una sola vez. Sin eso, abrir el
 *  registro con la conversión en marcha duplicaría el último medio segundo. */
function lineasDe(intento: IntentoConversion, vivas: LineaRegistro[]): LineaRegistro[] {
  if (!vivas.length) return intento.lineas;
  const vistas = new Set(intento.lineas.map((l) => l.n));
  return [...intento.lineas, ...vivas.filter((l) => !vistas.has(l.n))].sort((a, b) => a.n - b.n);
}

export function RegistroConversion({
  downloadId,
  titulo,
  abierto,
  onCerrar,
}: {
  downloadId: string;
  titulo: string;
  abierto: boolean;
  onCerrar: () => void;
}) {
  const observar = useEventos((s) => s.observarRegistro);
  const dejarDeObservar = useEventos((s) => s.dejarDeObservarRegistro);
  const vivo = useEventos((s) => s.registros[downloadId]);
  const revisionRegistro = useEventos((s) => s.revisionRegistro);

  const [elegido, setElegido] = useState<string | null>(null);

  // Solo se guardan las líneas en vivo de lo que alguien está mirando: mientras esta
  // ventana esté abierta, y ni un evento más.
  useEffect(() => {
    if (!abierto) return;
    observar(downloadId);
    return () => dejarDeObservar(downloadId);
  }, [abierto, downloadId, observar, dejarDeObservar]);

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['registro-conversion', downloadId],
    queryFn: () => api.registroConversion(downloadId),
    enabled: abierto,
  });

  // Un intento que empieza o termina cambia lo guardado: al cerrarse, la versión buena es
  // la de la base de datos y no la que se pidió antes de que acabara. La primera vuelta no
  // cuenta: la consulta de arriba acaba de pedirlo, y pedirlo dos veces al abrir es gratis
  // para nadie.
  const revisionVista = useRef(revisionRegistro);
  useEffect(() => {
    if (!abierto || revisionVista.current === revisionRegistro) return;
    revisionVista.current = revisionRegistro;
    void refetch();
  }, [revisionRegistro, abierto, refetch]);

  const intentos = data?.intentos ?? [];
  const intento = useMemo(
    () => intentos.find((i) => i.id === elegido) ?? intentos[0] ?? null,
    [intentos, elegido],
  );

  const lineas = useMemo(() => {
    if (!intento) return [];
    const vivas = vivo && vivo.runId === intento.id ? vivo.lineas : [];
    return lineasDe(intento, vivas);
  }, [intento, vivo]);

  // Autoscroll solo si ya estabas abajo: si has subido a leer algo, una línea nueva no
  // puede arrancarte de donde estás.
  const caja = useRef<HTMLDivElement>(null);
  const alFinal = useRef(true);
  useLayoutEffect(() => {
    const el = caja.current;
    if (el && alFinal.current) el.scrollTop = el.scrollHeight;
  }, [lineas.length]);

  const copiar = () => {
    void navigator.clipboard?.writeText(
      lineas.map((l) => `${horaDe(l.ts)} [${l.nivel}] ${l.texto}`).join('\n'),
    );
  };

  const estado = intento ? describirEstado(intento.estado) : null;
  const duracion = intento?.terminadoEn
    ? formatearDuracion(
        (new Date(intento.terminadoEn).getTime() - new Date(intento.iniciadoEn).getTime()) / 1000,
      )
    : null;

  return (
    <Dialog open={abierto} onClose={onCerrar} fullWidth maxWidth="lg">
      <DialogTitle sx={{ pb: 1 }}>
        Registro de conversión
        <Typography variant="body2" color="text.secondary" noWrap>
          {titulo}
        </Typography>
      </DialogTitle>

      <DialogContent>
        {error && <Alert severity="error">No se pudo leer el registro.</Alert>}

        {!error && !isPending && !intentos.length && (
          <Alert severity="info">
            Todavía no hay nada registrado de esta descarga. Aparecerá aquí en cuanto empiece a
            prepararse, línea a línea y sin esperar a que termine.
          </Alert>
        )}

        {intento && estado && (
          <>
            <Stack
              direction="row"
              spacing={1}
              sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 1, mb: 1.5 }}
            >
              {/* Los intentos anteriores se conservan: comparar el que falló con el que
                  salió bien es la mitad del diagnóstico cuando se han editado las pistas. */}
              {intentos.length > 1 && (
                <Select
                  size="small"
                  value={intento.id}
                  onChange={(e) => setElegido(e.target.value)}
                  sx={{ minWidth: 220 }}
                >
                  {intentos.map((i) => (
                    <MenuItem key={i.id} value={i.id}>
                      {`Intento ${i.numero} · ${new Date(i.iniciadoEn).toLocaleString('es-ES')}`}
                    </MenuItem>
                  ))}
                </Select>
              )}
              <Tooltip title={estado.nota}>
                <Chip size="small" color={estado.color} label={estado.texto} />
              </Tooltip>
              <Typography variant="caption" color="text.secondary">
                {describirOrigen(intento.origen)}
                {duracion ? ` · duró ${duracion}` : ''}
                {` · ${lineas.length} líneas`}
              </Typography>
            </Stack>

            {intento.truncado && (
              <Alert severity="info" sx={{ mb: 1 }}>
                Este registro era tan largo que se recortó por el medio: se conservan el
                principio —que es donde ffmpeg describe el fichero— y el final.
              </Alert>
            )}

            {intento.error && (
              <Alert severity="error" sx={{ mb: 1 }}>
                {intento.error}
              </Alert>
            )}

            <Box
              ref={caja}
              onScroll={(e) => {
                const el = e.currentTarget;
                alFinal.current = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
              }}
              sx={{
                maxHeight: '55vh',
                overflow: 'auto',
                bgcolor: 'action.hover',
                borderRadius: 1,
                p: 1.5,
                fontFamily: 'monospace',
                fontSize: 12,
                // Las líneas de ffmpeg son largas y no se pueden partir por cualquier
                // sitio: se dejan enteras y la caja hace scroll horizontal.
                whiteSpace: 'pre',
                overflowX: 'auto',
              }}
            >
              {lineas.map((l) => (
                <Box key={l.n} component="div" sx={{ color: colorDe(l.nivel) }}>
                  <Box component="span" sx={{ color: 'text.disabled' }}>
                    {horaDe(l.ts)}{' '}
                  </Box>
                  {l.texto}
                </Box>
              ))}
              {!lineas.length && (
                <Typography variant="caption" color="text.secondary">
                  Sin líneas todavía.
                </Typography>
              )}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={copiar} disabled={!lineas.length}>
          Copiar
        </Button>
        <Button onClick={onCerrar}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}
